import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../store/useAuth";
import {
  featureFlags,
  featureFlagById,
  evaluateFeatureFlags,
} from "../api/get";
import { postFeatureFlag } from "../api/post";
import { updateFeatureFlag } from "../api/patch";
import { deleteFeatureFlag } from "../api/delete";
import type {
  EvaluatedFeatureFlagsResponse,
  FeatureFlagQueryDto,
  FeatureFlagPagedResponse,
  FeatureFlagResponse,
  FeatureFlagCreate,
  FeatureFlagUpdate,
} from "../types/feature-flag";

export const useFeatureFlags = ({
  filters,
  enabled = true,
}: {
  filters?: FeatureFlagQueryDto;
  enabled?: boolean;
}) => {
  const { token } = useAuth();

  return useQuery<FeatureFlagPagedResponse>({
    queryKey: ["feature-flags", filters],
    queryFn: () => featureFlags(token, filters),
    enabled: !!token && enabled,
  });
};

export const useFeatureFlagById = (flagId: string | undefined) => {
  const { token } = useAuth();

  return useQuery<FeatureFlagResponse>({
    queryKey: ["feature-flags", flagId],
    queryFn: () => featureFlagById(token, flagId),
    enabled: !!token && !!flagId && flagId !== "new",
  });
};

export const useCreateFeatureFlag = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FeatureFlagCreate) => postFeatureFlag(data, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
    },
  });
};

export const useUpdateFeatureFlag = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      flagId,
      data,
    }: {
      flagId: string;
      data: FeatureFlagUpdate;
    }) => updateFeatureFlag(flagId, data, token),
    onSuccess: (_, { flagId }) => {
      queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
      queryClient.invalidateQueries({
        queryKey: ["feature-flags", flagId],
      });
    },
  });
};

export const useDeleteFeatureFlag = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (flagId: string) => deleteFeatureFlag(flagId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
    },
  });
};

export const useEvaluatedFeatureFlags = () => {
  const { token } = useAuth();

  return useQuery<EvaluatedFeatureFlagsResponse>({
    queryKey: ["feature-flags", "evaluate-me"],
    queryFn: () => evaluateFeatureFlags(token),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });
};
